import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import { omit } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styled from "styled-components";
import { v4 } from "uuid";
import { Form } from "../../../../components/forms";
import { Checkbox } from "../../../../components/forms/checkbox";
import { Country } from "../../../../components/forms/country";
import { FileField } from "../../../../components/forms/file-2";
import { Select } from "../../../../components/forms/select";
import { useFileLoader } from "../../../../components/hooks/file-loader";
import { Widget } from "../../../../components/widget";
import {
  useAddressAttachmentType,
  useIdentityAttachmentType,
} from "../../../../store/common/models";
import { VerificationDTO } from "../../../../store/verification/models/dto/kyc";
import { VerificationService } from "../../../../store/verification/service";
import { mapObject } from "../../../../utils/utils";
import {
  Section,
  StyledDateField,
  StyledTextField,
} from "../../../profile/base";
import { FraudRoutes } from "../../routes";
import { useNewIndividualKycCTA } from "./cta";

type RequestDTO = VerificationDTO.Requests.UploadIndividualKyc.T;
const { useFields, defaults } = VerificationDTO.Requests.UploadIndividualKyc;

const StyledWidget = styled(Widget)`
  height: auto;
  display: flex;
  flex-direction: column;
`;

export const NewIndividualKyc = () => {
  const { validation, labels } = useFields();
  const router = useRouter();

  const { handleSubmit, control, setValue } = useForm<RequestDTO>({
    defaultValues: useMemo(() => ({ ...defaults, fraudCheckId: v4() }), [
      defaults,
    ]),
    resolver: yupResolver(validation),
  });

  const addressAttachmentType = useAddressAttachmentType();
  const identityAttachmentType = useIdentityAttachmentType();

  const [enableBackgroundChecks, setEnableBackgroundChecks] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (!enableBackgroundChecks) {
      setValue("backgroundChecks.ongoingCheck", false as never);
    }
  }, [enableBackgroundChecks]);

  const cta = useNewIndividualKycCTA();

  const loader = useFileLoader();

  const handleSubmitCallback: (data: RequestDTO) => Promise<void> = useCallback(
    async (data) => {
      try {
        const result = await loader(
          VerificationService.postIndividualKYC(
            !enableBackgroundChecks
              ? omit(data, "backgroundChecks")
              : {
                  ...data,
                  backgroundChecks: {
                    ongoingCheck: data.backgroundChecks?.ongoingCheck || false,
                  },
                }
          )
        );

        if (result.status === "done") {
          router.stateService.go(FraudRoutes.names.allIndividualKyc);
        }
      } catch (e: any) {
        setValue("fraudCheckId", v4());
      }
    },
    [loader, router, setValue, enableBackgroundChecks]
  );

  return cta.fold(
    () => null,
    (x) => (
      <StyledWidget title="Individual KYC">
        <Form onSubmit={handleSubmit(handleSubmitCallback)}>
          <Section container spacing={3}>
            <Grid item xs={12} md={6} lg={3}>
              <Controller
                control={control}
                name="userDetails.firstName"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    fullWidth
                    {...field}
                    label="First Name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Controller
                control={control}
                name="userDetails.middleName"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    fullWidth
                    {...field}
                    label="Middle Name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Controller
                control={control}
                name="userDetails.lastName"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    fullWidth
                    {...field}
                    label="Last Name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Controller
                control={control}
                name="userDetails.dateOfBirth"
                render={({ field, fieldState }) => (
                  <StyledDateField
                    fullWidth
                    {...field}
                    label="Date of Birth"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <Controller
                control={control}
                name="userDetails.nationality"
                render={({ field, fieldState }) => (
                  <Country
                    fullWidth
                    {...field}
                    label="Nationality"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Section>
          <Section container spacing={3}>
            <Grid item xs={12} md={6} lg={9}>
              <Controller
                control={control}
                name="userDetails.address.streetAddress"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    fullWidth
                    {...field}
                    label="Street Address"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Controller
                control={control}
                name="userDetails.address.city"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    fullWidth
                    {...field}
                    label="City"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8} md={6} lg={4}>
              <Controller
                control={control}
                name="userDetails.address.state"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    fullWidth
                    {...field}
                    label="State"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={6} lg={3}>
              <Controller
                control={control}
                name="userDetails.address.zip"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    fullWidth
                    {...field}
                    label="ZIP Address"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={5}>
              <Controller
                control={control}
                name="userDetails.address.country"
                render={({ field, fieldState }) => (
                  <Country
                    fullWidth
                    {...field}
                    label="Country"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Section>
          <Section container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="faceAttachment"
                render={({ field, fieldState }) => (
                  <FileField
                    {...field}
                    label={labels.faceAttachment}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Section>
          <Section container spacing={3}>
            <Grid item xs={12} md={4}>
              <Controller
                control={control}
                name="identityDetails.attachment.type"
                render={({ field, fieldState }) => (
                  <Select
                    fullWidth
                    label="Identity Attachment Type"
                    options={mapObject(
                      identityAttachmentType.enum,
                      (value, key) => ({
                        title: value,
                        value: key,
                      })
                    )}
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                control={control}
                name="identityDetails.attachment.file"
                render={({ field, fieldState }) => (
                  <FileField
                    {...field}
                    label="Identity Attachment"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                control={control}
                name="identityDetails.additionalFile"
                render={({ field, fieldState }) => (
                  <FileField
                    {...field}
                    label="Additional Identity Attachment"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name="identityDetails.identity.number"
                render={({ field, fieldState }) => (
                  <StyledTextField
                    {...field}
                    fullWidth
                    label="Identity Document Number"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                control={control}
                name="identityDetails.identity.issueDate"
                render={({ field, fieldState }) => (
                  <StyledDateField
                    {...field}
                    fullWidth
                    label="Issue Date"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller
                control={control}
                name="identityDetails.identity.expiryDate"
                render={({ field, fieldState }) => (
                  <StyledDateField
                    {...field}
                    fullWidth
                    label="Expiry Date"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Section>
          <Section container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name="addressAttachment.type"
                render={({ field, fieldState }) => (
                  <Select
                    fullWidth
                    label="Address Attachment Type"
                    options={mapObject(
                      addressAttachmentType.enum,
                      (value, key) => ({
                        title: value,
                        value: key,
                      })
                    )}
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name="addressAttachment.file"
                render={({ field, fieldState }) => (
                  <FileField
                    {...field}
                    label="Address Attachment"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Section>
          <Section container spacing={3}>
            <Grid item xs={12}>
              <Checkbox
                onChange={() =>
                  setEnableBackgroundChecks(!enableBackgroundChecks)
                }
                checked={enableBackgroundChecks}
                label="Enable AML checks"
              />
              <Controller
                control={control}
                name="backgroundChecks.ongoingCheck"
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value || false}
                    label="Keep doing checks Regularly"
                    disabled={!enableBackgroundChecks}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}></Grid>
          </Section>
          {x}
        </Form>
      </StyledWidget>
    )
  );
};
